import { relativeTime } from '../../shared/lib/format';
import { FactList, FactRow } from '../../shared/ui/fact-list';
import { Banner, Section } from '../../shared/ui/layout';
import { Input } from '../../shared/ui/input';
import { Modal } from '../../shared/ui/modal';
import { Note } from '../../shared/ui/note';
import { Select } from '../../shared/ui/select';
import { TIMEOUT_SECONDS, type THttpMethod } from '../../entities/destination';
import { Hint, Label, Problem } from './field-bits';
import type { TDestinationSettings } from './use-destination-settings';

const METHODS: { value: THttpMethod; label: string }[] = [
  { value: 'POST', label: 'POST' },
  { value: 'PUT', label: 'PUT' },
  { value: 'PATCH', label: 'PATCH' },
  { value: 'DELETE', label: 'DELETE' },
  { value: 'GET', label: 'GET' },
];

export const DestinationName = ({ settings }: { settings: TDestinationSettings }) => {
  const { draft, change, problem, save } = settings;

  return (
    <>
      {save.isError && (
        <Banner detail={save.error instanceof Error ? save.error.message : undefined}>
          The settings were not accepted. Nothing was saved — the destination is still on its
          previous settings.
        </Banner>
      )}

      <Section title="What we call it">
        <Input
          className="max-w-form-narrow"
          aria-label="Name"
          invalid={Boolean(problem('name'))}
          value={draft.name}
          onChange={(event) => change({ name: event.target.value })}
        />
        <Problem>{problem('name')}</Problem>
      </Section>
    </>
  );
};

export const DestinationEndpoint = ({ settings }: { settings: TDestinationSettings }) => {
  const { draft, change, problem, destination } = settings;
  const sharing = destination.activity.flowsUsing;
  const noRate = !draft.ratePerSec.trim();

  return (
    <Section title="Where we send">
      <div className="grid max-w-form-narrow grid-cols-2 gap-4">
        <div className="col-span-2">
          <Label>URL we {draft.method} to</Label>
          <div className="flex gap-2">
            {/* A wrapper, not a class on Select: its own `w-full` wins over a width passed in. */}
            <div className="w-col-origin shrink-0">
              <Select
                aria-label="Method"
                value={draft.method}
                options={METHODS}
                onChange={(method) => change({ method })}
              />
            </div>
            <Input
              mono
              className="min-w-0 grow"
              aria-label={`URL we ${draft.method} to`}
              invalid={Boolean(problem('url'))}
              value={draft.url}
              onChange={(event) => change({ url: event.target.value })}
            />
          </div>
          <Problem>{problem('url')}</Problem>
        </div>

        <div>
          <Label>Wait for an answer, per attempt</Label>
          <Input
            aria-label="Wait for an answer, per attempt"
            inputMode="decimal"
            invalid={Boolean(problem('seconds'))}
            value={draft.seconds}
            trailing={<span className="text-meta text-faint">seconds</span>}
            onChange={(event) => change({ seconds: event.target.value })}
          />
          {problem('seconds') ? (
            <Problem>{problem('seconds')}</Problem>
          ) : (
            <Hint>
              {TIMEOUT_SECONDS.min}–{TIMEOUT_SECONDS.max} s
            </Hint>
          )}
        </div>

        <div id="rate-limit" className="scroll-mt-section">
          <Label>Never faster than</Label>
          <div className="flex items-center gap-2">
            <Input
              aria-label="Requests per second"
              className="min-w-0 flex-1"
              inputMode="numeric"
              placeholder="no limit"
              invalid={Boolean(problem('ratePerSec'))}
              value={draft.ratePerSec}
              trailing={<span className="text-meta text-faint">/ second</span>}
              onChange={(event) =>
                change({
                  ratePerSec: event.target.value,
                  ...(event.target.value.trim() ? {} : { rateBurst: '' }),
                })
              }
            />
            <Input
              aria-label="Burst"
              className="min-w-0 flex-1"
              inputMode="numeric"
              placeholder={noRate ? 'needs a rate' : 'same as rate'}
              disabled={noRate}
              invalid={Boolean(problem('rateBurst'))}
              value={draft.rateBurst}
              trailing={<span className="text-meta text-faint">burst</span>}
              onChange={(event) => change({ rateBurst: event.target.value })}
            />
          </div>
          <Problem>{problem('ratePerSec') ?? problem('rateBurst')}</Problem>
          <Hint>
            {sharing > 1
              ? `Shared by all ${sharing} flows that deliver here, not per flow.`
              : 'For this destination as a whole, whichever flow sends.'}
          </Hint>
        </div>

        <Note className="col-span-2">
          A timeout is ambiguous: the request may well have arrived, and the attempt is retried
          according to the step’s retry policy. A receiver should deduplicate on{' '}
          <span className="font-mono">idempotency-key</span>.
        </Note>
      </div>
    </Section>
  );
};

export const DestinationCli = ({
  settings,
  now,
}: {
  settings: TDestinationSettings;
  now: number;
}) => {
  const { draft, change, problem, destination } = settings;
  const session = destination.cliSession;

  return (
    <Section
      title="Who is listening"
      description={
        <>
          Events go to whichever machine runs{' '}
          <span className="font-mono text-primary">hookpipe listen</span> for this destination.
        </>
      }
    >
      <div className="flex max-w-form-narrow flex-col gap-4">
        <FactList>
          <FactRow label="Agent" tone={session?.connected ? 'success' : 'danger'}>
            {session?.connected
              ? `connected ${relativeTime(session.since, now)}`
              : 'nobody connected — events wait here until an agent does'}
          </FactRow>
          {session?.client && <FactRow label="Client">{session.client}</FactRow>}
          {session?.localTarget && (
            <FactRow label="Forwards to" mono>
              {session.localTarget}
            </FactRow>
          )}
          {!session?.connected && session?.lastSeenAt && (
            <FactRow label="Last seen">{relativeTime(session.lastSeenAt, now)}</FactRow>
          )}
        </FactList>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label>Label</Label>
            <Input
              aria-label="Label"
              placeholder={destination.name}
              value={draft.label}
              onChange={(event) => change({ label: event.target.value })}
            />
            <Hint>Only for telling agents apart on this screen.</Hint>
          </div>
          <div>
            <Label>Path on the local server</Label>
            <Input
              mono
              aria-label="Path on the local server"
              placeholder="the path it arrived on"
              invalid={Boolean(problem('path'))}
              value={draft.path}
              onChange={(event) => change({ path: event.target.value })}
            />
            <Problem>{problem('path')}</Problem>
          </div>
        </div>
      </div>
    </Section>
  );
};

// Every attempt reads the destination fresh, so a new URL or key reaches the retries too.
export const DestinationSaveConfirm = ({ settings }: { settings: TDestinationSettings }) => {
  const { confirm, closeConfirm, save, destination } = settings;
  if (!confirm) return null;

  const waiting = confirm.count.toLocaleString('en-US');
  const events = confirm.count === 1 ? 'event' : 'events';

  return (
    <Modal
      open
      onOpenChange={(open) => !open && closeConfirm()}
      width="md"
      title={`${waiting} ${events} still waiting here`}
      description="The change applies to them too, not only to what arrives next."
      submit={{
        label: 'Save',
        onSubmit: () => save.mutate(),
        waiting: save.isPending,
        waitingLabel: 'Saving',
      }}
    >
      <FactList>
        {confirm.urlChanged && (
          <FactRow label="Their next attempt" tone="motion">
            goes to the new URL
          </FactRow>
        )}
        {confirm.secretChanged && (
          <FactRow label="Signed with" tone="motion">
            both keys for 24 hours, then only the new one
          </FactRow>
        )}
      </FactList>
      {confirm.secretChanged && (
        <Note className="mt-2">
          {destination.name} can switch to the new key at any point in those 24 hours without
          rejecting a delivery.
        </Note>
      )}
    </Modal>
  );
};
